/**
 * Route chunk warming and navigation feedback.
 *
 * The point of this file is that navigation must never feel slower than it did
 * before splitting. Three mechanisms, in order of how often they save the day:
 *
 *  1. Idle warm-up — after the page has loaded and the browser goes idle, every
 *     route chunk is fetched one at a time, most-likely-next first. By the time
 *     a customer has read the menu, nothing is left to download.
 *
 *  2. Intent warming — hover, keyboard focus or touch on a link starts its
 *     chunk before the click lands. On desktop that is typically 200-600ms of
 *     head start; touchstart gives 100-300ms on mobile.
 *
 *  3. Progress feedback — if a click still beats the download, a top progress
 *     bar appears immediately. Without it the screen just sits there, because
 *     react-router v7 navigates inside startTransition and React keeps the old
 *     page on screen rather than showing a fallback.
 *
 * Everything here is attached with ONE capture-phase listener per event on the
 * document, so no navigation component had to change.
 */

import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { getEmbedConfig } from "../embed/config";
import { pagePatterns, pages, prefetchOrder, type PageKey } from "./lazyPages";
import { beginNavProgress, endNavProgress } from "./navProgress";

interface NetworkInformation {
  effectiveType?: string;
  saveData?: boolean;
}

/** Warming chunks over a metered or 2G connection costs the user money. */
function shouldSkipWarmUp(): boolean {
  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return (
    connection.effectiveType === "slow-2g" || connection.effectiveType === "2g"
  );
}

/**
 * Turns a link href into a path the router understands.
 *
 * Standalone the app lives under /olo/{storeId}; embedded with hash routing the
 * route is the fragment. Returns null for anything that is not an in-app route:
 * external links, mailto, tel, and the plain "#anchor" links react-scroll uses.
 */
function toRoutePath(href: string | null): string | null {
  if (!href) return null;

  const config = getEmbedConfig();

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;

  if (config.routerMode === "hash") {
    if (url.pathname !== window.location.pathname) return null;
    const hash = url.hash.replace(/^#/, "");
    // "#offers" is an on-page anchor; "#/offers" is a route.
    return hash.startsWith("/") ? hash : null;
  }

  const base = config.basename;
  if (base && !url.pathname.startsWith(base)) return null;

  return url.pathname.slice(base.length) || "/";
}

function findPage(path: string): PageKey | null {
  for (const key of Object.keys(pagePatterns) as PageKey[]) {
    for (const pattern of pagePatterns[key]) {
      if (matchPath({ path: pattern, end: true }, path)) return key;
    }
  }
  return null;
}

function pageForEvent(target: EventTarget | null): PageKey | null {
  if (!(target instanceof Element)) return null;

  const anchor = target.closest("a[href]");
  if (!anchor) return null;

  const path = toRoutePath(anchor.getAttribute("href"));
  if (!path) return null;

  return findPage(path);
}

/** Imperative warm-up, for navigations triggered by a button rather than a link. */
export function prefetchPage(key: PageKey): Promise<void> {
  return pages[key].preload();
}

export function useRoutePrefetch(): void {
  const location = useLocation();

  // --- 1. idle warm-up -----------------------------------------------------
  useEffect(() => {
    if (shouldSkipWarmUp()) return;

    let cancelled = false;
    let timer: number | undefined;

    // preload() never rejects, so "resolved" does not mean "loaded". Without
    // tracking what we have already tried, a chunk that fails permanently would
    // be picked again on every pass and re-requested every 300ms forever.
    const attempted = new Set<PageKey>();

    const warmNext = (queue: PageKey[]): void => {
      if (cancelled) return;

      const key = queue.find(
        (k) => !attempted.has(k) && !pages[k].isLoaded(),
      );
      if (!key) return;

      attempted.add(key);

      pages[key].preload().then(() => {
        if (cancelled) return;
        // A gap between chunks so warming never competes with menu images or
        // an in-flight API call for bandwidth.
        timer = window.setTimeout(() => warmNext(queue), 300);
      });
    };

    const start = () => {
      if (cancelled) return;
      const idle = (
        window as Window & {
          requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
        }
      ).requestIdleCallback;

      if (idle) idle(() => warmNext([...prefetchOrder]), { timeout: 3000 });
      else timer = window.setTimeout(() => warmNext([...prefetchOrder]), 1500);
    };

    // Wait for first paint and the initial data to settle before spending
    // bandwidth on routes the customer has not asked for yet.
    if (document.readyState === "complete") {
      timer = window.setTimeout(start, 800);
    } else {
      window.addEventListener("load", start, { once: true });
    }

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("load", start);
    };
  }, []);

  // --- 2. intent warming + 3. click feedback -------------------------------
  useEffect(() => {
    const warmFromEvent = (event: Event) => {
      const key = pageForEvent(event.target);
      if (key && !pages[key].isLoaded()) void pages[key].preload();
    };

    const onClick = (event: MouseEvent) => {
      // Let modified clicks fall through to the browser.
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const key = pageForEvent(event.target);
      if (!key) return;

      // Already in memory: navigation is synchronous, so no bar.
      if (pages[key].isLoaded()) return;

      beginNavProgress(pages[key].preload());
    };

    // Capture phase, so we still see the event if a handler stops propagation.
    const options = { capture: true, passive: true } as const;

    document.addEventListener("mouseover", warmFromEvent, options);
    document.addEventListener("focusin", warmFromEvent, options);
    document.addEventListener("touchstart", warmFromEvent, options);
    document.addEventListener("click", onClick, { capture: true });

    return () => {
      document.removeEventListener("mouseover", warmFromEvent, options);
      document.removeEventListener("focusin", warmFromEvent, options);
      document.removeEventListener("touchstart", warmFromEvent, options);
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, []);

  // Location only commits once the chunk has rendered, so this is a reliable
  // "we got there" signal — and a safety net if a preload promise never settles.
  useEffect(() => {
    endNavProgress();
  }, [location.pathname]);
}
